import { Component, OnInit } from '@angular/core';
import { Keys, StorageService } from '../../service/storage.service';
import { LoanDetailsService } from '../../service/loan-details.service';
import { WriteOffService } from '../../service/write-off.service';
import { MatTableDataSource } from '@angular/material';
import { Loan } from '../../model/loan.model';
import { EmiStatus } from '../../model/emi-status';
import { LoanSettlementService } from '../../service/loan-settlement.service';
import * as moment from 'moment';
import { WriteOffTag } from '../../model/WriteOff';
import { PrepaymentStatus } from '../../model/prepayment-status';
import { animate, state, style, transition, trigger } from '@angular/animations';

export interface LenderSettlementLoan {
    loanId: string;
    rating: string;
    tenure: number;
    investmentInLoan: number;
    loanDisbursalDate: string;
    grossCashFlowReceivedBeforeSettlement: number;
    principalOutstandingBeforeSettlement: number;
    settlementDate: string;
    settlementAmount: number;
    grossCashFlowReceivedPostSettlement: number;
    settlementPrincipalWrittenOff: number;

}

@Component({
    selector: 'app-lender-settled-loans',
    templateUrl: './lender-settled-loans.component.html',
    styleUrls: ['./lender-settled-loans.component.scss'],
    animations: [
        trigger('detailExpand', [
            state('collapsed', style({ height: '0px', minHeight: '0' })),
            state('expanded', style({ height: '*', minHeight: "*" })),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ],

})
export class LenderSettledLoansComponent implements OnInit {
    public displayedColumns = ['loanId',
        'investmentInLoan',
        'grossCashFlowReceived',
        'principalOutstandingBeforeSettlement',
        'settlementDate',
        'settlementPrincipal',
        'grossCashFlowReceivedPostSettlement',
        'settlementPrincipalWrittenOff'
    ];
    settlementDataSource = new MatTableDataSource();
    networkCallInProgress: boolean;

    private storageService: StorageService;
    private readonly loanDetailService: LoanDetailsService;
    private readonly writeOffService: WriteOffService;
    private readonly loanSettlementService: LoanSettlementService;
    expandedElement: any | null;
    public isMobile: boolean = false;
    screenWidth: number;

    constructor(storageService: StorageService, loanDetailService: LoanDetailsService, writeOffService: WriteOffService,
        loanSettlementService: LoanSettlementService) {
        this.storageService = storageService;
        this.loanDetailService = loanDetailService;
        this.writeOffService = writeOffService;
        this.loanSettlementService = loanSettlementService;
    }
    ngAfterViewInit() {
        this.screenWidth = window.innerWidth;
        this.isMobile = (<any>window).innerWidth < 500;
        if (this.isMobile) {
            this.displayedColumns = ['loanId',
                'investmentInLoan',
                'grossCashFlowReceived','accordion_arrow'];
        }
    }

    async ngOnInit(): Promise<void> {
        this.networkCallInProgress = true;
        const lenderId = this.storageService.getItem(Keys.LENDER_ID);
        const settledLoans = await this.loanDetailService.getSettledLoansFor(lenderId);
        this.settlementDataSource.data = await this.populateSettlementData(settledLoans, lenderId);
        this.networkCallInProgress = false;
    }

    private async populateSettlementData(settledLoans: Loan[], lenderId: string): Promise<LenderSettlementLoan[]> {
        const lenderSettlementLoans: LenderSettlementLoan[] = [];
        for (let i = 0; i < settledLoans.length; i++) {
            const loan = settledLoans[i];
            const investmentDetails = loan.lenders_invested.find(investment => investment.lender_id === lenderId);
            const lenderShare = investmentDetails.invested_amount / loan.loan_fulfilled_amount;
            const loanSettlement = (await this.loanSettlementService.getByLoanId(loan._id))[0];
            const grossCashFlowReceivedBeforeSettlement = this.getGrossCashFlowReceived(loan);
            lenderSettlementLoans.push({
                loanId: loan.generated_loan_id,
                rating: loan.rating,
                tenure: loan.loan_tenure,
                investmentInLoan: investmentDetails.invested_amount,
                loanDisbursalDate: moment(loan.loan_disbursed_on).format('DD MMM YYYY'),
                grossCashFlowReceivedBeforeSettlement: grossCashFlowReceivedBeforeSettlement * lenderShare,
                principalOutstandingBeforeSettlement: loanSettlement.dues.principal * lenderShare,
                settlementDate: moment(loanSettlement.settlementDateTime).format('DD MMM YYYY'),
                settlementAmount: loanSettlement.oneTimeSettlement.totalAmount * lenderShare,
                grossCashFlowReceivedPostSettlement: (grossCashFlowReceivedBeforeSettlement + loanSettlement.oneTimeSettlement.totalAmount) * lenderShare,
                settlementPrincipalWrittenOff: await this.getWrittenOffPrincipal(loan) * lenderShare
            });
        }
        return lenderSettlementLoans;
    }

    private async getWrittenOffPrincipal(loan) {
        const writeOff = await this.writeOffService.getWriteOffByLoanId(loan._id);
        if (!writeOff) {
            return 0;
        }
        const settlementDetails = writeOff.history.find(history => history.tag === WriteOffTag.Settlement);
        return settlementDetails ? settlementDetails.principal : 0;
    }

    private getGrossCashFlowReceived(loan): number {
        let grossCashFlowReceived = 0;
        loan.emi_schedule.forEach(emi => {
            if ((emi.status === EmiStatus.Received || emi.status === EmiStatus.Completed)) {
                const penalInterest = emi.penalInterest ? emi.penalInterest : 0;
                grossCashFlowReceived += emi.emi_amount + penalInterest;
            }
        });
        if (loan.prepayment_details) {
            loan.prepayment_details.details.forEach(payment => {
                if (payment.prepayment_status === PrepaymentStatus.PartpaymentCompleted || payment.prepayment_status === PrepaymentStatus.PrepaymentCompleted) {
                    grossCashFlowReceived += payment.prepayment_principal + payment.prepayment_interest;
                }
            });
        }

        return grossCashFlowReceived;
    }
}
